import {IncomingMessage, OutgoingMessage, IncomingHttpHeaders} from 'http';
import https from 'https';
import axios, {AxiosError, Method} from 'axios';
import {TFunction} from 'i18next';
import {
  ApiResultCode,
  IApiError,
  IProcessApiErrorCallback,
  needShowAlert,
  processApiError,
} from './api/apiError';
import {pushLog} from './debug';
import {IPAlertParam} from '../component/PAlert';
import {IS_CLIENT, API_HOST, AUTH_HOST} from '../constant';
import {getClientStore} from '../store';
import {PStore} from '../type';
import {PAlert} from '../type/alert';

const httpsAgent = new https.Agent({
  keepAlive: true,
});

export interface IParamApi {
  url: string;
  apiHost?: string;
  method?: Method;
  headers?: IncomingHttpHeaders;
  params?: {[key: string]: any} | URLSearchParams;
  data?: object | string;
  req?: IncomingMessage;
  res?: OutgoingMessage;
  onClickErrorAlert?: (status: number, erros?: IApiError) => void;
  processApiErrorCallback?: IProcessApiErrorCallback;
  lang?: string;
  store?: PStore;
}

// TODO: @root 인터페이스 컨벤션 맞추기
export interface I_ApiResult<T, M = I_ApiMeta> {
  success: boolean;
  data?: T;
  meta?: M;
  error?: AxiosError;
  alert?: PAlert;
  resultCode: number;
  resultMessage: string;
}

export interface I_ApiMeta {
  [key: string]: any;
  pagination?: {
    totalCount?: number;
    offset?: number;
    cursor?: string;
    limit?: number;
    last?: boolean;
  };
}

axios.interceptors.response.use(response => {
  // errors가 없으면 실제 에러
  if (getIsErrorStatus(response.status) && !response.data?.errors?.[0]) {
    const error = new Error('errors is not exist') as AxiosError;
    // 의도한 4xx, 5xx가 아닌 경우 발생한 에러정보가 없기 때문에, catch에서 로그를 남기기 위해 response를 넣어준다.
    error.response = response;
    return Promise.reject(error);
  }
  return response;
});
export default function callApi<T = any, M = I_ApiMeta>({
  url,
  apiHost = API_HOST,
  method = 'get',
  headers = {},
  params,
  data,
  req,
  res,
  onClickErrorAlert,
  processApiErrorCallback,
  lang,
  store,
}: IParamApi): Promise<I_ApiResult<T, M>> {
  if (IS_CLIENT) {
    if (Object.prototype.hasOwnProperty.call(apiCounter, url)) {
      apiCounter[url] += 1;
    } else {
      apiCounter[url] = 1;
    }
  }

  if (req) {
    if (req.headers.cookie) {
      headers.cookie = req.headers.cookie;
    }
    headers['user-agent'] = req.headers['user-agent'];
  }

  const isAuth = apiHost === AUTH_HOST;
  const isExternalHost = apiHost !== API_HOST && !isAuth;
  if (!isExternalHost) {
    headers['accept-language'] = lang;
  }

  // makeFetchSaga 가 lang, headers 정보를 주입하고 있는데, 리액트 쿼리는 직접 입력해야 합니다.
  const _store = IS_CLIENT ? getClientStore() : store;
  const commonState = _store?.getState().common;
  if (commonState?.headers) {
    if (commonState.headers['cookie']) {
      headers['cookie'] = commonState.headers['cookie'];
    }
    headers['user-agent'] = commonState.headers['user-agent'];
  }
  if (commonState && !isExternalHost) {
    headers['accept-language'] = commonState.lang;
  }

  return axios({
    url,
    method,
    baseURL: apiHost,
    headers,
    params,
    withCredentials: !isExternalHost,
    data,
    // 내부 에러인 경우에도 4xx, 5xx 상태코드를 반환한다.
    validateStatus: status =>
      getIsSuccessStatus(status) || getIsErrorStatus(status),
    /**
     * prod 환경에서 일반적인 상황일 때 api 레이턴시가 5초 이상 넘어가지 않지만,
     * qa 환경에서는 6초 이상 걸릴 수 있으므로 백엔드(liam.bin)와 협의한 6초로 정의한다.
     * https://kakaopagecorp.slack.com/archives/C0185N27PRQ/p1701766867212479?thread_ts=1701416214.389759&cid=C0185N27PRQ
     */
    timeout: 6000,
    httpsAgent,
  })
    .then(({headers, data, status}) => {
      if (res) {
        const setCookie = headers['set-cookie'];
        setCookie && res.setHeader('set-cookie', setCookie);
      }

      // 외부 API를 호출한 경우에는 무조건 성공으로 처리한다
      if (isExternalHost) {
        return {
          success: true,
          data,
          resultCode: ApiResultCode.Success,
          resultMessage: 'success',
        };
      }

      const isSuccess = getIsSuccessStatus(status);
      const error: IApiError | undefined = data?.errors?.[0];
      const isMaintenance = error?.errorType === 'SERVICE_MAINTENANCE';
      const resultCode = isSuccess
        ? ApiResultCode.Success
        : isMaintenance
          ? ApiResultCode.ServiceMaintenance
          : error?.code ?? status;
      const resultMessage = isSuccess ? 'success' : error?.errorMessage ?? '';
      const showAlert = !isSuccess && needShowAlert(error);

      pushLog({msg: `callApi ${url}`, params: {resultCode}});

      if (!isSuccess && !showAlert && !isMaintenance) {
        processApiError({error, processApiErrorCallback});
      }

      pushLog({
        msg: `callApi ${url}`,
        params: data.data,
        type: isSuccess ? 'info' : 'error',
      });

      return {
        success: isSuccess,
        data: isMaintenance ? error?.notice : data.data,
        meta: data.meta,
        resultCode,
        resultMessage,
        alert: showAlert
          ? ({
              title: undefined, // TODO: alert에 title이 필수값인데, optional로 바꿔도 되는지 알아본 다음 삭제하자
              desc: resultMessage,
              labelConfirm: (t: TFunction) => t('common_ok'),
              callback: onClickErrorAlert
                ? () => onClickErrorAlert(status, error)
                : () => processApiError({error, processApiErrorCallback}),
            } as IPAlertParam)
          : undefined,
      };
    })
    .catch((ex: AxiosError) => {
      if (ex.code === 'ECONNABORTED') {
        // 클라이언트가 임의로 요청을 끊는 등의 이유로 발생할 가능성이 많은 에러이므로, 아무런 처리 하지 않음
      } else if (!ex.response && ex.message === 'Network Error') {
        // iOS Safari에서 간혹 low level network error를 줌. 원인은 브라우저만 안다고 함.
        // (https://github.com/axios/axios/blob/master/lib/adapters/xhr.js#L78 request.onerror 부분 주석 참고)
      } else {
        console.error('critical error: ' + ex);
        // TODO: 에러가 많이 쌓이고 있어서 임시로 막음
        // reportError(ex);
      }

      // 어떤 로그인지 좀 더 파악하기 위해 ex.code, ex.response.data, ex.response.status 값 추가
      ex.message = `callApi catch: ex=${ex}, url=${url}, method=${method}, params=${JSON.stringify(
        params
      )}, code=${ex.code}, headers=${JSON.stringify(headers)}, data=${
        typeof data === 'object' ? JSON.stringify(data) : data
      }, response_data=${
        typeof ex.response?.data === 'object'
          ? JSON.stringify(ex.response?.data)
          : ex.response?.data
      }, response_status=${ex.response?.status}`;

      return {
        success: false,
        error: ex,
        resultCode: ApiResultCode.UnexpectedError,
        resultMessage: 'unexpected error',
      };
    })
    .then(result => {
      if (IS_CLIENT) {
        apiCounter[url] -= 1;
        if (apiCounter[url] === 0) {
          delete apiCounter[url];
        }
      }
      return result;
    });
}

function getIsSuccessStatus(status: number) {
  return status >= 200 && status < 300;
}

function getIsErrorStatus(status: number) {
  return status >= 400;
}

const apiCounter: {[key: string]: number} = {};

interface ICallApiListResult extends I_ApiResult<any> {
  results: Array<I_ApiResult<any>>;
}
export function callApiList(
  params: Array<Omit<IParamApi, 'processApiErrorCallback'>>
): Promise<ICallApiListResult> {
  let isApiErrorProcessed = false;
  const processApiErrorCallback: IProcessApiErrorCallback = () => {
    const res = isApiErrorProcessed;
    isApiErrorProcessed = true;
    return res;
  };
  return Promise.all(
    params.map(item => ({...item, processApiErrorCallback})).map(callApi)
  ).then((results: Array<I_ApiResult<any>>) => {
    const failedResult = results.find(item => !item.success);
    const resultCode = failedResult
      ? failedResult.resultCode
      : results[0].resultCode;
    const resultMessage = failedResult
      ? failedResult.resultMessage
      : results[0].resultMessage;
    return {
      success: !failedResult,
      resultCode,
      resultMessage,
      data: results.map(item => item.data),
      meta: results.map(item => item.meta),
      results,
    };
  });
}
